import { parseRange, renderRange, type RangeValue } from "@/types/range-code";
import type { SwadeModConfigurationInterface } from "@/types/items";

const MELEE: RangeValue = { kind: 'melee' };

/**
 * Increments for arithmetic. Melee counts as 0 and `Self` as 1; every other token has no numeric
 * reading, so additive arithmetic against it is rejected and the base is returned unchanged.
 */
function toIncrements(value: RangeValue): [number, number, number] | null {
    switch (value.kind) {
        case 'increments':
            return value.increments;
        case 'scalar':
            return [value.value, value.value * 2, value.value * 4];
        case 'melee':
            return [0, 0, 0];
        case 'token':
            // `Self` counts as 1 — scaled by any multiplier it carries, so `Self x2` reads as 2.
            return value.text.toLowerCase() === 'self'
                ? [value.multiplier, value.multiplier * 2, value.multiplier * 4]
                : null;
    }
}

const VECTOR_PART_PATTERN: RegExp = /^\d+(?:\.\d+)?$/;

/** A vector adjustment: exactly three slash-separated numbers, validated as the grammar does. */
function parseVector(raw: string | number | null): [number, number, number] | null {
    if (typeof raw !== 'string' || !raw.includes('/')) return null;

    const parts = raw.split('/').map((part) => part.trim());
    if (parts.length !== 3 || parts.some((part) => !VECTOR_PART_PATTERN.test(part))) return null;

    const numbers = parts.map(Number);
    return [numbers[0], numbers[1], numbers[2]];
}

/**
 * The one place a modification value becomes a number. Anything that is not a non-blank string or
 * a finite number is REJECTED rather than coerced: `Number(null)`, `Number('')`, `Number('   ')`,
 * `Number(false)` and `Number([])` are all 0, so coercing would let one malformed modification
 * blank a field.
 */
export function parseScalar(raw: unknown): number | null {
    if (typeof raw !== 'number' && typeof raw !== 'string') return null;
    if (typeof raw === 'string' && raw.trim() === '') return null;

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * A modification value that was never filled in. Distinct from a BASE of `null` or `''`, which
 * legitimately means "no range recorded" and parses to Melee — `parseRange` is right to accept
 * those, but only for a base.
 */
function isBlankValue(raw: unknown): boolean {
    if (typeof raw === 'string') return raw.trim() === '';
    return typeof raw !== 'number';
}

/** Multiply preserves shape — increments stay increments, a scalar stays a scalar. */
function applyMultiply(value: RangeValue, raw: string | number | null): RangeValue {
    // A multiply whose value is an increment list is malformed and contributes nothing. Only
    // additive adjustments take a vector form.
    if (typeof raw === 'string' && raw.includes('/')) return value;

    const factor = parseScalar(raw);
    if (factor === null) return value;

    // A negative multiplier is refused outright rather than applied: the grammar's multiplier
    // pattern admits no sign, so `Smarts x-2` would re-parse as a token literally named that.
    // Refusing means leaving the value untouched — not resetting a multiplier the base already had.
    if (factor < 0) return value;

    switch (value.kind) {
        case 'melee':
            return MELEE;
        case 'scalar':
            return { kind: 'scalar', value: value.value * factor };
        case 'increments':
            return {
                kind: 'increments',
                increments: [
                    value.increments[0] * factor,
                    value.increments[1] * factor,
                    value.increments[2] * factor,
                ],
            };
        case 'token':
            return { ...value, multiplier: value.multiplier * factor };
    }
}

/**
 * Additive always yields an increment triple. Scalars are summed and applied to short, which then
 * regenerates medium and long as 2x and 4x; vectors are summed and applied positionally afterwards.
 * Summing before applying is what keeps this stage independent of array order.
 */
function applyAdditive(
    value: RangeValue,
    configurations: SwadeModConfigurationInterface[],
): RangeValue {
    const scalarDeltas: number[] = [];
    const vectorDeltas: [number[], number[], number[]] = [[], [], []];
    let sawAny = false;

    for (const configuration of configurations) {
        const sign = configuration.operator === 'subtract' ? -1 : 1;

        const vector = parseVector(configuration.value);
        if (vector) {
            vectorDeltas[0].push(sign * vector[0]);
            vectorDeltas[1].push(sign * vector[1]);
            vectorDeltas[2].push(sign * vector[2]);
            sawAny = true;
            continue;
        }

        const scalar = parseScalar(configuration.value);
        if (scalar !== null) {
            scalarDeltas.push(sign * scalar);
            sawAny = true;
        }
    }

    if (!sawAny) return value;

    // Sort before summing, exactly as the multiply stage sorts before reducing. Float addition is
    // not associative, so accumulating in array order is order-dependent — and regeneration then
    // amplifies the error 2x and 4x before the floor: [-100, -4.3, +93.8] on 12/24/48 gives 1/3/6
    // while [-100, +93.8, -4.3] gives 1/2/5.
    const total = (deltas: number[]): number =>
        [...deltas].sort((a, b) => a - b).reduce((sum, delta) => sum + delta, 0);

    const scalarDelta = total(scalarDeltas);
    const vectorDelta: [number, number, number] = [
        total(vectorDeltas[0]),
        total(vectorDeltas[1]),
        total(vectorDeltas[2]),
    ];

    const increments = toIncrements(value);
    // A token with no numeric reading — Touch, Cone, Smarts — rejects additive arithmetic.
    if (increments === null) return value;

    const short = increments[0] + scalarDelta;
    const regenerated: [number, number, number] = scalarDelta !== 0
        ? [short, short * 2, short * 4]
        : [increments[0], increments[1], increments[2]];

    return {
        kind: 'increments',
        increments: [
            regenerated[0] + vectorDelta[0],
            regenerated[1] + vectorDelta[1],
            regenerated[2] + vectorDelta[2],
        ],
    };
}

/** Floor every increment; if any reaches zero the whole range collapses to melee. */
function collapse(value: RangeValue): RangeValue {
    if (value.kind === 'scalar') {
        const floored = Math.floor(value.value);
        return floored <= 0 ? MELEE : { kind: 'scalar', value: floored };
    }

    if (value.kind === 'token') {
        // Zero collapses to melee, as it does for increments. A fractional multiplier is kept:
        // a token is a formula reference rather than a literal distance, so `Smarts x0.5` reads
        // as "half your Smarts die" and round-trips through the grammar.
        return value.multiplier === 0 ? MELEE : value;
    }

    if (value.kind !== 'increments') return value;

    const floored = value.increments.map((n) => Math.floor(n));
    if (floored.some((n) => n <= 0)) return MELEE;

    return { kind: 'increments', increments: [floored[0], floored[1], floored[2]] };
}

export function resolveRange(
    base: unknown,
    configurations: SwadeModConfigurationInterface[],
): string | number | undefined {
    let current = parseRange(base);
    if (current === null) return undefined;

    const all = configurations ?? [];

    // Stage 1 — alter replaces the base outright; the last one wins. A blank value is REFUSED:
    // ChoiceSetConfigurator seeds {operator: null, value: null}, so an author who selects "alter"
    // before typing would otherwise blank the field to Melee. `alter 0` still says Melee outright.
    // A replacement typed as a bare number reads as increments (`alter 8` → `8/16/32`, the design's
    // fixture): this is the ONE stage that turns a flat range into increments by itself. Multiply
    // keeps whatever shape it is given (owner's ruling 2026-09-24 — a flat 6 doubled is a flat 12,
    // never 12/24/48; the Range modifier's own text says a flat range or Smarts is simply 2× or 3×),
    // and the additive stage regenerates increments on its own.
    const alters = all.filter((configuration) => configuration.operator === 'alter');
    if (alters.length > 0) {
        const raw = alters[alters.length - 1].value;
        const replacement = isBlankValue(raw) ? null : parseRange(raw);
        if (replacement !== null) {
            current = replacement.kind === 'scalar'
                ? { kind: 'increments', increments: [replacement.value, replacement.value * 2, replacement.value * 4] }
                : replacement;
        }
    }

    // Stage 2 — multiply. Collect every factor, sort, reduce to ONE product, then apply once.
    // Folding factors into the value one at a time is NOT order-independent: float multiplication
    // is commutative but not associative, so 9 x 1.2 x 2.5 floors to 26 while 9 x 2.5 x 1.2
    // floors to 27. Reducing a sorted factor list to a single product removes both the ordering
    // and the intermediate rounding.
    const factors = all
        .filter((configuration) => configuration.operator === 'multiply')
        .map((configuration) => (
            typeof configuration.value === 'string' && configuration.value.includes('/')
                ? null
                : parseScalar(configuration.value)
        ))
        .filter((factor): factor is number => factor !== null && factor >= 0)
        .sort((a, b) => a - b);

    if (factors.length > 0) {
        current = applyMultiply(current, factors.reduce((product, factor) => product * factor, 1));
    }

    // Stage 3 — additive.
    current = applyAdditive(
        current,
        all.filter((configuration) =>
            configuration.operator === 'add' || configuration.operator === 'subtract'),
    );

    // Each stage decides the shape it hands on, so a flat range that only a multiply touched — or
    // that nothing touched — renders flat. Until 2026-09-24 a closing reference comparison expanded
    // ANY scalar a stage had reallocated into `v/2v/4v`, which turned `6 × 2` into `12/24/48`.
    return renderRange(collapse(current));
}
